import { useEffect, useRef, useState } from 'react';

import { Button } from '@gearbelt/ui/components';

import { StarIcon } from '@/components/icons/star';
import { StarSolidIcon } from '@/components/icons/star-solid';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';

const LOW_SCORE_THRESHOLD = 4;
const OTHER_TAG = 'Other';

export interface FeedbackPayload {
  surface: string;
  rating: number;
  tags: string[];
  comment: string;
  submittedAt: string;
}

export interface FeedbackPromptProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Surface key used in Segment event properties. e.g. "deployment-rules". */
  surface: string;
  heading: string;
  question?: string;
  /** Multi-choice tags shown on low score. The last tag should be "Other" (requires comment when selected). */
  tags: string[];
  dismissible: boolean;
  onSubmit?: (payload: FeedbackPayload) => void;
  onDismiss?: (state: { surface: string; ratingAtDismiss: number; tagsAtDismiss: string[]; hadComment: boolean }) => void;
}

export function FeedbackPrompt({ open, onOpenChange, surface, heading, question, tags, dismissible, onSubmit, onDismiss }: FeedbackPromptProps) {
  const toast = useToast();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [comment, setComment] = useState('');
  const submittedRef = useRef(false);

  // Delayed reset so the modal can animate out before state clears.
  useEffect(() => {
    if (!open) {
      const reset = setTimeout(() => {
        setRating(0);
        setHoveredRating(0);
        setSelectedTags(new Set());
        setComment('');
        submittedRef.current = false;
      }, 200);
      return () => clearTimeout(reset);
    }
  }, [open]);

  const isLowScore = rating > 0 && rating <= LOW_SCORE_THRESHOLD;
  const isHighScore = rating > LOW_SCORE_THRESHOLD;
  const otherSelected = selectedTags.has(OTHER_TAG);
  const commentRequired = isLowScore && otherSelected;

  const canSubmit = (() => {
    if (rating === 0) return false;
    if (isHighScore) return true;
    if (selectedTags.size === 0) return false;
    if (commentRequired && !comment.trim()) return false;
    return true;
  })();

  const handleSubmit = () => {
    if (!canSubmit) return;
    submittedRef.current = true;
    onSubmit?.({
      surface,
      rating,
      tags: Array.from(selectedTags),
      comment: comment.trim(),
      submittedAt: new Date().toISOString(),
    });
    toast.success('Feedback submitted successfully');
    onOpenChange(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && open && !submittedRef.current) {
      onDismiss?.({
        surface,
        ratingAtDismiss: rating,
        tagsAtDismiss: Array.from(selectedTags),
        hadComment: comment.trim().length > 0,
      });
    }
    onOpenChange(next);
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          if (!dismissible) e.preventDefault();
        }}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}>
        <DialogTitle hideCloseButton={!dismissible}>{heading}</DialogTitle>

        <div className="flex flex-col gap-2">
          {question && <p className="text-sm">{question}</p>}
          <div className="flex items-center justify-center gap-2" onMouseLeave={() => setHoveredRating(0)} role="radiogroup" aria-label="Rating">
            {[1, 2, 3, 4, 5].map((value) => {
              const filled = (hoveredRating || rating) >= value;
              const StarComponent = filled ? StarSolidIcon : StarIcon;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={rating === value}
                  aria-label={`${value} star${value === 1 ? '' : 's'}`}
                  onMouseEnter={() => setHoveredRating(value)}
                  onClick={() => setRating(value)}
                  className="focus-visible:ring-default-blue -mx-1 rounded p-1 transition-transform hover:scale-110 focus-visible:ring-2 focus-visible:outline-none">
                  <StarComponent size={40} className={cn('transition-colors', filled ? 'text-default-yellow' : 'text-grey-3')} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Low-score path: required multi-choice tags */}
        {isLowScore && (
          <div className="flex flex-col gap-2">
            <p className="text-sm">
              Select one or more of what was missing <span className="text-secondary-text">(Required)</span>
            </p>
            <div className="flex flex-col gap-1.5">
              {tags.map((tag) => {
                const selected = selectedTags.has(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    aria-pressed={selected}
                    className={cn(
                      'focus-visible:ring-default-blue rounded border px-3 py-2 text-left text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none',
                      selected
                        ? 'border-default-blue bg-default-light-blue text-default-dark-blue'
                        : 'border-grey-3 text-default-text hover:bg-grey-1 bg-white',
                    )}>
                    {tag}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Comment field is always shown once a rating is selected; required only when low-score + "Other". */}
        {rating > 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-sm">
              What can be improved?
              {commentRequired && <span className="text-secondary-text"> (Required)</span>}
            </p>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tell us what was missing"
              rows={3}
              autoFocus={commentRequired}
            />
          </div>
        )}

        {rating > 0 && (
          <DialogFooter>
            <Button disabled={!canSubmit} onClick={handleSubmit}>
              Submit
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
